import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Commands, People, Search } from '@local/client-contracts';
import { Constants } from '@local/common';
import { isMac } from '@local/common-web';
import { KeyName, getModifiers, isEnterKey } from '@local/ts-infra';
import { PopupRef } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { EventsService } from '@shared/services';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { Logger } from '@unleash-tech/js-logger';
import { take } from 'rxjs';
import { ComponentFocused } from 'src/app/bar/models/component-focused.model';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { ContextMenuInvokeSource } from 'src/app/bar/services/commands/results-item-context-menu-helper';
import { MethodsList } from 'src/app/bar/services/home-page.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { PeopleCommandBuilder } from 'src/app/bar/views/results-views/people/people-command-builder.service';
import { PeopleContextMenuHelper } from 'src/app/bar/views/results-views/people/people-context-menu-helper';
import { ResultContextMenuItem, TelemetryTrigger } from 'src/app/bar/views/results/models/results-types';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { PreviewMode } from '../../../model/preview-mode';
import { ContactLinkViewModel } from '../../models/contact-link-view.model';
import { PeopleService } from '../../services/people.service';
import { WorkspacesService } from 'src/app/bar/services';

@UntilDestroy()
@Component({
  selector: 'preview-header',
  templateUrl: './preview-header.component.html',
  styleUrls: ['./preview-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewHeaderComponent implements AfterViewInit, OnDestroy, OnInit {
  private activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;
  private readonly COMP_INDEX = 1;
  private _componentFocusedIndex: number;
  protected workspacesService: WorkspacesService = inject(WorkspacesService);

  disableOpen: boolean;
  markedIndex: number;
  methodsList: MethodsList = [];
  _model: Search.ResultResourceItem;

  @Input() set model(val: Search.ResultResourceItem) {
    this._model = val;
  }

  get model() {
    return this._model;
  }

  @Input() set details(value: People.PersonDetails) {
    this._details = value;
    if (this.details?.contactLinks) {
      this.buildContactLinks();
    }
  }

  @Input() size: PreviewMode;
  @Input() personIndex: number;
  @Input() isLauncher: boolean;
  @Input() searchId: string;
  @Input() set isFavorite(val: boolean) {
    if (!this.model) return;
    this.model.isFavorite = val;
  }
  @Input() set componentFocused(val: ComponentFocused) {
    this._componentFocusedIndex = val.compIndex;
    if (val?.compIndex === this.COMP_INDEX) {
      this.markedIndex = val?.direction === 'first' ? 0 : this.methodsList.length - 1;
      this.setMethodsList();
      return;
    }
  }
  @Input() smallWidthPreview: boolean = true;
  @Input() previewPopupLarge: boolean;
  @Input() extraSmallWidthPreview: boolean;
  @Output() closePopup: EventEmitter<void> = new EventEmitter();
  @Output() componentFocusedExit: EventEmitter<string> = new EventEmitter<string>();
  @Output() readyForFocus: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('subtitle', { read: ElementRef }) subtitleRef: ElementRef<HTMLElement>;
  @ViewChild('title', { read: ElementRef }) titleRef: ElementRef<HTMLElement>;
  @ViewChild('actions', { read: ElementRef }) actionsRef: ElementRef;
  @ViewChild('openIcon', { read: ElementRef }) openRef: ElementRef;
  @ViewChildren('linkElement', { read: ElementRef }) linksRef: QueryList<ElementRef>;
  @ViewChild('emailElement', { read: ElementRef }) emailRef: ElementRef;

  private _details: People.PersonDetails;
  get details(): People.PersonDetails {
    return this._details;
  }

  contextMenuHelper: PeopleContextMenuHelper;
  private logger: Logger;
  contactLinks: Record<string, ContactLinkViewModel> = {};

  private keyHandlerId: string;
  contextMenuItems: Array<ResultContextMenuItem>;
  DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG = Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG;

  private get eventResources() {
    return [
      {
        id: this.model?.id,
        list: 'cloud_search_results',
        appId: this.model?.link?.appId,
        type: this.model?.resource?.type,
        position: this.personIndex,
        linkId: this.model?.link?.id,
      },
    ];
  }

  constructor(
    private hostRef: ElementRef,
    private contextMenuService: ContextMenuService,
    private errorHandler: GlobalErrorHandler,
    private cdr: ChangeDetectorRef,
    public peopleService: PeopleService,
    private eventService: EventsService,
    private peopleBuilder: PeopleCommandBuilder,
    private resourcesService: ResourcesService,
    private commandsService: CommandsService,
    private keyboardService: KeyboardService,
    private flagsService: FlagsService,
    private resultCommandService: ResultCommandService,
    private hubService: HubService
  ) {}

  ngOnInit(): void {
    this.initDisableOpen();
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
      this.handleKeys(keys, event);
    }, 6);
  }

  ngOnDestroy(): void {
    this.unregisterKeyHandler();
    if (this.contextMenuHelper.ref) this.contextMenuHelper.ref.destroy();
  }

  ngAfterViewInit(): void {
    this.contextMenuHelper = new PeopleContextMenuHelper(
      this.peopleBuilder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService
    );
    this.contextMenuHelper.invoke$.pipe(untilDestroyed(this)).subscribe(({ item, trigger }) => this.emitContextMenuCommand(item, trigger));
    this.peopleService.popupRef?.close$.pipe(take(1)).subscribe(() => {
      this.contextMenuHelper.ref?.destroy();
    });
    if (this.model.type === 'result') {
      this.contextMenuHelper.buildItemsRequest(null, this.model, 'actions').then((item) => {
        this.peopleBuilder.build(item).then((res) => (this.contextMenuItems = res));
      });
    }
    this.setMethodsList();
    if (this.methodsList) {
      this.readyForFocus.emit(this.COMP_INDEX);
    }
  }

  async initDisableOpen() {
    this.disableOpen = !this.model.view?.title?.onClick || (await this.flagsService.isEnabled(Constants.DISABLED_PEOPLE_ACTION_FLAG));
  }

  unregisterKeyHandler() {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
  }

  copyValue(event: MouseEvent, valueToCopy: any) {
    let openAt: { x: number; y: number } = event;
    if (!openAt.x && !openAt.y) {
      const { width, left, top, height } = this.actionsRef.nativeElement.getBoundingClientRect();
      if (width && height) {
        openAt = { x: left + width / 2, y: top / 2 + height };
      }
    }
    const items: ContextMenuItem[] = [
      {
        id: 'copy_job_title',
        text: 'Copy Job Title',
        command: { type: 'copy-clipboard', value: valueToCopy } as Commands.CopyClipboard,
        icon: { type: 'font-icon', value: 'icon-copy' },
      },
    ];
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ContextMenuItem) =>
          this.resultCommandService.executeCommand(item.command, { item: this.model, searchId: this.searchId }, 'result', {
            location: { title: this.hubService.currentLocation },
            label: 'copy_job_title',
            target: 'mouse_click',
            resources: this.eventResources,
          }),
      },
      { position: 'below' }
    );
    this.cdr.markForCheck();
    this.activeContextMenu.close$.pipe(untilDestroyed(this)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }
  private async buildContactLinks() {
    this.contactLinks = await this.peopleService.buildContactLinks(this.details, this.searchId);
    this.cdr.markForCheck();
    this.setMethodsList();
  }

  private emitContextMenuCommand(item: ResultContextMenuItem, trigger: TelemetryTrigger): void {
    let label: string = item.id;
    if (label === 'favorite') {
      this.model.isFavorite = !this.model.isFavorite;
      this.resultCommandService.toggleFavorite(this.model, this.eventResources, trigger, this.searchId);
      this.cdr.markForCheck();
      return;
    }
    this.resultCommandService.executeCommand(item.command, { item: this.model, searchId: this.searchId }, 'person', {
      target: trigger,
      location: { title: this.peopleService.currentLocation },
      label,
      resources: this.eventResources,
    });
    this.eventService.event('context_menu.open_from_preview', {
      label,
      location: { title: this.peopleService.currentLocation },
      resources: this.eventResources,
    });
  }

  async openContextMenu(event: MouseEvent, source: ContextMenuInvokeSource) {
    this.eventService.event('context_menu.open_from_preview', {
      label: 'open_menu',
      location: { title: 'people' },
      resources: this.eventResources,
    });
    const ref = await this.contextMenuHelper.open(event, this.model, source);
    ref.destroy$.pipe(untilDestroyed(this)).subscribe(() => this.cdr.markForCheck());
  }

  openUrl() {
    this.commandsService.executeCommand(this.model.view.title.onClick);
  }

  onHeaderClick() {
    const openItem = this.contextMenuItems.find((i) => i.id === 'open');
    this.resultCommandService.executeCommand(openItem.command, { item: this.model, searchId: this.searchId }, 'person', {
      target: 'mouse_click',
      location: { title: this.peopleService.currentLocation },
      label: 'open',
      resources: this.eventResources,
    });
  }

  setMethodsList() {
    this.methodsList = [];
    if (this.titleRef?.nativeElement) this.methodsList.push({ id: 'title', fn: () => this.openUrl() });
    const keysLinks: string[] = Object.keys(this.contactLinks);
    if (keysLinks?.length)
      this.methodsList.push(
        ...keysLinks.map((key) => {
          return { id: key, fn: () => this.peopleService.directMessage(this.contactLinks[key]) };
        })
      );
    if (this.emailRef?.nativeElement) this.methodsList.push({ id: 'email', fn: () => this.peopleService.sendEmail(this.details) });
    if (this.openRef?.nativeElement) this.methodsList.push({ id: 'open', fn: () => this.openUrl() });
    if (this.actionsRef?.nativeElement) this.methodsList.push({ id: 'actions', fn: () => this.actionsRef.nativeElement.click() });
  }

  async handleKeys(keys: Array<KeyName>, event): Promise<void> {
    const modifiers = getModifiers(keys);
    if (this.COMP_INDEX === this._componentFocusedIndex) {
      if (keys[0] === 'tab') {
        event.stopPropagation();
        event.preventDefault();
        if (modifiers.length && modifiers[0] === 'shift') {
          if (this.markedIndex === 0) {
            this.componentFocusedExit.emit('prev');
          }
          this.markedIndex = (this.markedIndex - 1) % this.methodsList.length;
        } else if (this.markedIndex === this.methodsList.length - 1) {
          this.componentFocusedExit.emit('next');
          this.markedIndex = -1;
        } else this.markedIndex = (this.markedIndex + 1) % this.methodsList.length;
        this.cdr.markForCheck();
        return;
      }
      if (isEnterKey(keys[0])) {
        this.methodsList[this.markedIndex].fn();
        event.stopPropagation();
        return;
      }
    }

    if (isMac()) keys = keys.map((k) => (k = k === 'control' ? 'command' : k));
    const matchingIdx =
      this.contextMenuItems?.findIndex(
        (item) => item?.shortcut && item?.shortcut?.length === keys.length && item.shortcut.every((key) => keys.includes(key))
      ) || -1;

    if (matchingIdx !== -1 && !keys.includes('enter')) {
      this.emitContextMenuCommand(this.contextMenuItems[matchingIdx], 'context_menu_keyboard');
      setTimeout(() => {
        this.cdr.markForCheck();
      }, 0);
      event.stopPropagation();
      event.preventDefault();
      return;
    }
  }
}
